import { createContext, Dispatch, Reducer, useReducer } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import reducer, {
  State,
  isAuthenticated,
  isMinor1317,
  hasSelectedAccount,
  defaultState,
} from './state/reducer';
import { Action } from './state/actions';
import Container from './Container';
import { AuthenticatedState } from './utils/types';
import SessionExpiryWarning from './SessionExpiryWarning';
import Unauthenticated from './Unauthenticated';
import { ErrorBoundary } from '@sentry/react';
import ErrorPage from './pages/ErrorPage';
import Accounts from './login/Accounts';
import AccountSwitchWarning from './AccountSwitchWarning';
import MinorManagedCarePage from './pages/MinorManagedCarePage';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { ldInitConfig, useFlagCheck } from '@/utils/use-feature-flags';
import UnderMaintenance from '@/pages/UnderMaintenance';
import { useAuthAndAccountSetup } from './utils/use-auth-and-account-setup';

export const StateContext = createContext<{
  state: AuthenticatedState;
  dispatch: Dispatch<Action>;
}>({
  state: {} as any,
  dispatch: () => {
    console.error('dispatch called before initialization');
  },
});

export const MobileContext = createContext<boolean>(false);

const App = () => {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(
    reducer,
    defaultState,
  );

  function shouldShowMaintenancePage(): boolean {
    const { isAllowed } = useFlagCheck();
    return isAllowed({
      patientPortalShowMaintenancePage: true,
    });
  }

  const [checkingAuth] = useAuthAndAccountSetup(dispatch);

  if (checkingAuth) {
    return null;
  }

  const mobile = window.matchMedia('(max-width: 640px)').matches;

  /* eslint-disable no-nested-ternary */
  return (
    <ErrorBoundary fallback={<ErrorPage />}>
      <MobileContext.Provider value={mobile}>
        {shouldShowMaintenancePage() ? (
          <UnderMaintenance />
        ) : isAuthenticated(state) ? (
          isMinor1317(state) ? (
            <StateContext.Provider value={{ state, dispatch }}>
              <MinorManagedCarePage />
            </StateContext.Provider>
          ) : hasSelectedAccount(state) ? (
            <StateContext.Provider value={{ state, dispatch }}>
              <Container />
              <SessionExpiryWarning />
              <AccountSwitchWarning />
            </StateContext.Provider>
          ) : (
            <StateContext.Provider value={{ state, dispatch }}>
              <Router>
                <Accounts />
              </Router>
            </StateContext.Provider>
          )
        ) : (
          <Unauthenticated state={state} dispatch={dispatch} />
        )}
      </MobileContext.Provider>
    </ErrorBoundary>
  );
  /* eslint-enable no-nested-ternary */
};

export default withLDProvider(ldInitConfig)(App);
